import {
  Mask,
  Spinner,
  View,
  IconXLine,
  IconButton,
  Text,
  Flex,
} from '@smarterservices/smarter-ui';
import { useEffect, useState, useRef, useLayoutEffect } from 'react';
import { useParams } from 'react-router-dom';

import useAppStore from '../../stores/app';
import useSessionStore from '../../stores/session';
import useDataBroker from '../../stores/dataBroker';
import useLayoutStore from '../../stores/layout';
import Checkin from '../../components/Checkin';
import RoomOptions from '../../components/Options';
import RoomSettings from '../../components/RoomSettings';
import SettingsModal from '../../components/RoomSettings/SettingsModal';
import Focus from '../../components/Focus';
import SessionGrid from '../../components/SessionGrid';
import RoomNavBar from '../../components/NavBar';
import HeaderBar from '../../components/HeaderBar';
import TransferModal from '../../components/Modal/Transfer';
import EndSessionModal from '../../components/Modal/End';
import AlreadyInRoomOverlay from '../../components/Overlays/AlreadyInRoomOverlay';
import RoomChatModal from '../../components/Modal/RoomChat';
import PageLoader from '../../components/Common/PageLoader';
import config from '../../config';
import { useVtcHotkeys } from '../../hooks/useVtcHotkeys';
import { SessionViewerProvider } from '../../contexts/SessionViewerContext';
import Sounds from '../../components/Sounds';
import AppNotifications from '../../components/DesktopApp/AppNotifications';
import VtcRoomConnection from '../../components/VtcRoomConnection';
import * as atatus from 'atatus-spa';


const VirtualTestCenter = () => {
  // Get roomId from route parameters
  const { roomId } = useParams();

  // Use selective subscriptions to prevent unnecessary re-renders
  const room = useAppStore((state) => state.room);
  const toggleMagnification = useAppStore((state) => state.toggleMagnification);
  const mediaRoom = useAppStore((state) => state.mediaRoom);
  const mediaClient = useAppStore((state) => state.mediaClient);
  const closeDrawer = useAppStore((state) => state.closeDrawer);
  const drawerMode = useAppStore((state) => state.drawerMode);
  const isDrawerOpen = useAppStore((state) => state.isDrawerOpen);
  const joinRoom = useAppStore((state) => state.joinRoom);

  const RoomContainerRef = useRef(null);
  const RoomContentRef = useRef(null);
  const DrawerLayoutRef = useRef(null);
  const initialStateLoaded = useSessionStore((state) => state.initialStateLoaded);
  const updateLoader = useSessionStore((state) => state.updateLoader);
  const transferSessionSid = useSessionStore((state) => state.transferSessionSid);

  const [alreadyInRoom, setAlreadyInRoom] = useState(false);
  const [isRoomConnected, setIsRoomConnected] = useState(false);

  // Register VTC keyboard shortcuts
  useVtcHotkeys({ toggleMagnification });

  useLayoutEffect(() => {
    if (RoomContentRef) {
      useLayoutStore.getState().initialize({
        regions: {
          roomContainer: RoomContainerRef,
          roomContent: RoomContentRef,
          drawer: DrawerLayoutRef,
        },
      });
      //useLayoutStore.getState().calculateLayoutSizing();
    }
  }, [RoomContainerRef]);

  useEffect(() => {
    // Add window resize listener to ensure layout recalculation
    const handleWindowResize = () => {
      useLayoutStore.getState().debouncedLayoutUpdate();
    };

    const initializeVTC = async () => {
      // if the room is not set, and there is a room in the path, try to join it...
      if (roomId && !isRoomConnected) {
        const joinResult = await joinRoom(roomId);
        if (!joinResult.successful) {
          setAlreadyInRoom(true);
        } else {
          // indicates where the data should be persisted, this can be either local for testing or remote...
          const dataMode = config?.vtc?.mode ?? 'remote';
          useDataBroker.getState().setDataMode(dataMode);
          const initResult = await useDataBroker.getState().initialize(dataMode);

          if (initResult.successful) {
            // Initialize audio devices from localStorage or defaults
            await useAppStore.getState().initializeAudioDevices();

            // Anything else that has to be done upon successful initialization...
            updateLoader(true);

            atatus.setCustomData({
              roomSid: useAppStore.getState()?.roomSid,
              userSid: useAppStore.getState()?.user?.sid,
            });

            atatus.setUser(useAppStore.getState()?.user?.sid,
              useAppStore.getState()?.user?.email,
              useAppStore.getState()?.user?.firstName + ' ' + useAppStore.getState()?.user?.lastName);

            if (room && room.roomName) {
              document.title = `${room.roomName} - Virtual Test Center`;
            } else {
              document.title = 'Virtual Test Center';
            }

            // if token is in the query string, make sure it is removed...
            const url = new URL(window.location.href);
            url.searchParams.delete('token');
            window.history.replaceState(null, '', url);

            window.addEventListener('resize', handleWindowResize);

          } else {
            // TODO: There was an issue initializing....

          }

        };
      }
    }

    initializeVTC();

    // Cleanup on unmount to prevent memory leaks
    return () => {
      window.removeEventListener('resize', handleWindowResize);
      useLayoutStore.getState().cleanup();
      useDataBroker.getState().cleanup();
    };
  }, []);

  const DrawerContainer = ({ children }) => (
    <View
      as="div"
      width="100%"
      height="100%"
      borderWidth="none small none none"
      borderColor="brand"
    >
      {children}
    </View>
  );

  /**
   * Returns the JSX component corresponding to the current drawer mode.
   *
   * @returns {JSX.Element|null} The component to render inside the drawer,
   *                             or null if the drawer mode is not recognized.
   */
  const getDrawerContent = () => {
    switch (drawerMode) {
      case 'checkin':
        return (
          <DrawerContainer>
            {renderDrawerHeader({ title: 'Queue', closeButton: true })}
            <Checkin />
          </DrawerContainer>
        );
      case 'focus':
        return <Focus />;
      case 'settings':
        return (
          <DrawerContainer>
            {renderDrawerHeader({ title: 'Settings', closeButton: true })}
            <RoomSettings />
          </DrawerContainer>
        );
      case 'options':
        return (
          <DrawerContainer>
            {renderDrawerHeader({ title: room.roomName, closeButton: true })}
            <RoomOptions />
          </DrawerContainer>
        );
      default:
        return null;
    }
  };

  const renderDrawerHeader = (options) => {
    return (
      <Flex
        width="100%"
        justifyContent="space-between"
        alignItems="center"
        padding="x-small small"
      >
        <Flex.Item shouldGrow>
          <Text size="large" color="primary">
            {options.title}
          </Text>
        </Flex.Item>
        <Flex.Item shouldGrow textAlign="end">
          {options.closeButton && (
            <IconButton
              withBackground={false}
              withBorder={false}
              size="small"
              screenReaderLabel="Unread Messages"
              margin="none"
              padding="none"
              onClick={closeDrawer}
            >
              <IconXLine />
            </IconButton>
          )}
        </Flex.Item>
      </Flex>
    );
  };

  if (alreadyInRoom) {
    return (<AlreadyInRoomOverlay />);
  }

  return (
    <>
      {/* Hidden component that manages VTC proctor room connection */}
      <VtcRoomConnection onConnectionChange={setIsRoomConnected} />

      <SessionViewerProvider>
        {/* Sounds component listens for sound triggers from the store */}
        <Sounds />

        <Flex as="div" height="100vh" width="100vw" direction="column">
          {/* Horizontal Menu Bar */}
          <HeaderBar />

          {/* Main Content Area */}
          <Flex.Item height={'calc(100vh - 40px)'} shouldGrow shouldShrink>
            <Flex as="div" height="100%" width="100%">
              <Flex.Item as="div" height="100%">
                <RoomNavBar />
              </Flex.Item>

              <Flex.Item
                as="div"
                id="drawer-content"
                width={isDrawerOpen ? config.vtc.modes[drawerMode].drawerWidth : 0}
                height="100%"
                elementRef={(el) => {
                  DrawerLayoutRef.current = el;
                  useLayoutStore.getState().setDrawerRef(el);
                }}

                background="secondary"
                position="relative"
                overflowX="hidden"
                overflowY="auto"
              >
                {getDrawerContent()}
              </Flex.Item>
              <Flex.Item
                as="div"
                id="room-content"
                elementRef={(el) => {
                  RoomContentRef.current = el;
                }}
                height={'calc(100vh - 40px)'}
                maxHeight={'calc(100vh - 40px)'}
                background="secondary"
                shouldGrow
                shouldShrink
                overflowY="hidden"
                overflowX="hidden"
              >
                {!initialStateLoaded && (
                  <Mask
                    placement="center"
                    fullscreen={true}
                    themeOverride={{ zIndex: 9999 }}
                  >
                    <Spinner renderTitle="Loading" size="large" />
                    <Text size="large" color="primary">
                      Configuring Room...
                    </Text>
                  </Mask>
                )}
                {initialStateLoaded && <SessionGrid />}
              </Flex.Item>
            </Flex>
          </Flex.Item>
        </Flex>

        {transferSessionSid && <TransferModal />}
        <EndSessionModal />
        <SettingsModal />
        <PageLoader />
        <RoomChatModal />
        <AppNotifications />
      </SessionViewerProvider>
    </>
  );
};
export default VirtualTestCenter;
